import { useCallback } from "react";
import type { CreateSessionFailed } from "@b2bportal/auth-api";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import type { PaymentError } from "@b2bportal/purchase-api";
import {
  GetPointsBalanceErrorCategory,
  type ModelError,
} from "@b2bportal/smcc-accounts-api";
import { useI18nContext } from "@hopper-b2b/i18n";

const RBC_CONTACT_URL = "#"; // Placeholder - needs to be updated

// TODO(Sarah): Confirm and update error states + copy - currently referencing SMCC copy
export const useRewardsErrorMessage = () => {
  const { t } = useI18nContext();

  const getCreateSessionError = useCallback(
    (error: CreateSessionFailed | undefined) => {
      if (error == null) {
        return undefined;
      }

      const { errorMessage, errorCode, reason } = error;
      // errorMessage is an enum key
      switch (errorMessage) {
        case "WaitAndRetry":
          return t("rewards.createSession.WaitAndRetry", {
            errorCode,
          });
        case "CccmkServerInMaintenance":
          return t("rewards.createSession.CccmkServerInMaintenance", {
            errorCode,
          });
        case "VPointsContactSupport":
          return t("rewards.createSession.VPointsContactSupport", {
            errorCode,
            contactFormURL: RBC_CONTACT_URL,
          });
        default:
          break;
      }

      // If we arrive here, we couldn't find the appropriate error message.
      // Return the error.reason
      return reason;
    },
    [t]
  );

  const getPointsBalanceError = useCallback(
    (error: ModelError | undefined) => {
      if (error == null) {
        return undefined;
      }
      switch (error.errorCategory) {
        case GetPointsBalanceErrorCategory.WaitAndRetry:
          return t("rewards.getPointsBalance.WaitAndRetry", {
            errorCode: error.errorStatus,
          });
        case GetPointsBalanceErrorCategory.CccmkServerInMaintenance:
          return t("rewards.getPointsBalance.CccmkServerInMaintenance", {
            errorCode: error.errorStatus,
          });
        case GetPointsBalanceErrorCategory.VPointsContactSupport:
          return t("rewards.getPointsBalance.VPointsContactSupport", {
            errorCode: error.errorStatus,
            contactFormURL: RBC_CONTACT_URL,
          });
        default:
          ensureExhaustive(error.errorCategory, undefined);
      }
    },
    [t]
  );

  const getPaymentError = useCallback(
    (error: PaymentError): string | undefined => {
      if (error.value.value?.RewardsPaymentError === "RewardsErrorCode") {
        const statusCode = error.value.value?.code;
        if (statusCode == null) {
          return;
        }

        const errorCode = error.value.value?.message;

        switch (statusCode) {
          case "WaitAndRetry":
            return t("rewards.payment.WaitAndRetry", {
              errorCode,
            });

          case "CccmkServerInMaintenance":
            return t("rewards.payment.CccmkServerInMaintenance", {
              errorCode,
            });

          case "VPointsContactSupport":
            return t("rewards.payment.VPointsContactSupport", {
              errorCode,
              contactFormURL: RBC_CONTACT_URL,
            });

          case "WaitForTomorrowRefund":
            return t("rewards.payment.WaitForTomorrowRefund", {
              errorCode,
            });
        }
        return;
      }
    },
    [t]
  );

  const getCancellationError = useCallback(
    (error: PaymentError): string | undefined => {
      if (error.value.value?.RewardsPaymentError === "RewardsErrorCode") {
        const statusCode = error.value.value?.code;
        if (statusCode == null) {
          return;
        }

        const errorCode = error.value.value?.message;

        switch (statusCode) {
          case "WaitAndRetry":
            return t("rewards.cancellation.WaitAndRetry", {
              errorCode,
            });

          case "CccmkServerInMaintenance":
            return t("rewards.cancellation.CccmkServerInMaintenance", {
              errorCode,
            });

          case "VPointsContactSupport":
            return t("rewards.cancellation.VPointsContactSupport", {
              errorCode,
              contactFormURL: RBC_CONTACT_URL,
            });

          case "WaitForTomorrowRefund":
            return t("rewards.cancellation.WaitForTomorrowRefund", {
              errorCode,
            });
        }
        return;
      }
    },
    [t]
  );

  return {
    getCreateSessionError,
    getPointsBalanceError,
    getPaymentError,
    getCancellationError,
  };
};
